import { useMemo, useState } from 'react';
import { Plus, Search } from 'lucide-react';
import type { Service } from '@repo/api-client/dtos';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { displayWireMoney } from '@/features/money';

interface ServicePickerDialogProps {
  open: boolean;
  onOpenChange: (next: boolean) => void;
  services: Service[];
  /** Selecting a service appends it as a line item in the invoice currency. */
  onPick: (service: Service) => void;
  /**
   * Caller wants to create a brand-new service. We close this dialog
   * and the caller decides what to show next (likely
   * LineItemDialog with "save to library" pre-checked).
   */
  onCreateNew: () => void;
}

export function ServicePickerDialog({
  open,
  onOpenChange,
  services,
  onPick,
  onCreateNew,
}: ServicePickerDialogProps) {
  const [query, setQuery] = useState('');

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (q === '') return services;
    return services.filter((s) => s.name.toLowerCase().includes(q));
  }, [services, query]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Add a service</DialogTitle>
          <DialogDescription>
            Pick from your saved services, or create a new one.
          </DialogDescription>
        </DialogHeader>

        <div className="relative">
          <Search className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search services"
            className="pl-9"
            autoFocus
          />
        </div>

        <div className="flex max-h-[55vh] flex-col gap-1 overflow-y-auto">
          {/* The "create new" affordance lives as the first row of
              the list, not in the footer — mobile's pattern. Putting
              it in the footer alongside "Cancel" made it read like
              "select a service first, then tap to create", which is
              the opposite of the intent. */}
          <button
            type="button"
            onClick={onCreateNew}
            className="flex items-center gap-3 rounded-lg border border-dashed border-line bg-secondary/20 px-3 py-2.5 text-left transition-colors hover:border-line-strong hover:bg-secondary/40"
          >
            <span className="flex size-8 items-center justify-center rounded-md bg-accent-soft/60 text-foreground">
              <Plus className="size-4" />
            </span>
            <span className="flex flex-col">
              <span className="text-sm font-medium text-foreground">
                Create new service
              </span>
              <span className="text-[11px] text-muted-foreground">
                Save it to your library for next time
              </span>
            </span>
          </button>

          {filtered.length === 0 ? (
            <p className="px-2 py-6 text-center text-sm text-muted-foreground">
              {services.length === 0
                ? 'No saved services yet.'
                : `No services match "${query}".`}
            </p>
          ) : (
            filtered.map((s) => (
              <button
                key={s.id}
                type="button"
                onClick={() => {
                  onPick(s);
                  onOpenChange(false);
                }}
                className="flex items-center justify-between gap-3 rounded-lg border border-transparent px-3 py-2 text-left hover:border-line hover:bg-secondary/40"
              >
                <div className="flex min-w-0 flex-col">
                  <span className="truncate text-sm font-medium text-foreground">{s.name}</span>
                  {s.description ? (
                    <span className="truncate text-xs text-muted-foreground">{s.description}</span>
                  ) : null}
                </div>
                <div className="flex shrink-0 items-baseline gap-1.5">
                  <span className="font-mono text-sm tabular-nums text-foreground">
                    {displayWireMoney(s.pricingRate, s.pricingCurrency)}
                  </span>
                  <span className="text-[11px] text-muted-foreground">/ {s.pricingUnit}</span>
                </div>
              </button>
            ))
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
